import EzController from 'controllers/ez-base-controller';
import { getAjaxModal, insertAjaxContent } from 'lib/ajax-helpers';
import { ezConfirm } from 'lib/modal_helpers';
import { getCurPopCount } from 'lib/download-limit-helpers';
import { getContentType, triggerCustomEvent } from 'lib/helpers';
import {
  sendTrackingEvent,
  findCategoryByElement,
  trackCompleteEvent,
} from 'components/analytics/conversions/tracking';
import {
  DOWNLOAD_CLICK,
  DOWNLOAD_COMPLETE,
  JOIN_PRO_TRIGGERED,
  JOIN_PRO_COMPLETE,
} from 'components/analytics/conversions/constants/actions';
import events from 'lib/events';
import { addSessionDownloadActivity } from 'lib/sessions-manager';
import { getResourceType } from 'lib/resource-show-helpers';
import Turbolinks from 'turbolinks';

export default class DownloadButtonPricingController extends EzController {
  static targets = ['downloadButton']

  static values = {
    dailyDownloadCount: Number,
    dailyDownloadLimit: Number,
    inFreeTrial: Boolean,
  };

  connect() {
    if (!this.isTurbolinksPreview) {
      const searchData = window.localStorage.getItem(`resource-${this.resourceId}`);
      if (searchData) {
        this.searchData = JSON.parse(searchData);
        window.localStorage.removeItem(`resource-${this.resourceId}`);
      }
      this.flagLoaded();
    }
  }

  freeDownload = (event) => {
    event.preventDefault();

    if (this.hasHitDownloadLimit()) {
      this.openDownloadLimitModal();
    } else if (this.downloadButtonTarget.dataset.dlAdModalPath) {
      this.showDownloadAdPopup();

      if (this.downloadButtonTarget.getAttribute('data-license-type') !== 'editorial') {
        this.showFreeAttributionSlidedown();
      }
    } else {
      this.triggerDownload();
    }
  }

  showFreeAttributionSlidedown() {
    // first, get rid of any existing slidedowns
    if (document.querySelector('[data-controller="free-attribution-slidedown"]')) {
      const existingSlidedown = document.querySelector('[data-controller="free-attribution-slidedown"]');
      existingSlidedown.remove();
    }

    const resourceId = document.querySelector('[data-controller*="resource-show-main"]').getAttribute('data-resource-id');

    const slidedownParent = document.querySelector('.ez-page-wrapper__content');

    insertAjaxContent(`/resources/${resourceId}/show_free_attr_slidedown`,
      slidedownParent,
      'afterbegin',
      () => {
        triggerCustomEvent(events.show_page.reveal_free_attribution_slidedown);
      });
  }

  get curPopCount() {
    return getCurPopCount();
  }

  hasHitDownloadLimit() {
    if (document.body.dataset.userIsPro === 'true') {
      return false;
    }

    return this.curPopCount >= 3;
  }

  triggerDownload = (event, reloadPage = false) => {
    if (this.hasHitDownloadLimit()) {
      this.openDownloadLimitModal();
    }

    addSessionDownloadActivity(this.resourceId);
    const downloadFileUrl = (event && (typeof event.detail.downloadFileUrl !== 'undefined')) ? event.detail.downloadFileUrl : this.downloadButtonTarget.getAttribute('href');

    if (reloadPage) {
      window.open(downloadFileUrl, '_blank');

      trackCompleteEvent({
        action: DOWNLOAD_COMPLETE,
        label: getResourceType(),
        category: 'Show',
        dimensions: {
          content_type: `Content-${getContentType()}`,
        },
      });

      window.setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      sendTrackingEvent({
        element: this.element,
        data: {
          action: DOWNLOAD_COMPLETE,
          label: getResourceType(),
          category: 'Show',
          content_type: `Content-${getContentType()}`,
        },
      });

      window.location.href = downloadFileUrl;
    }
  }

  showDownloadAdPopup() {
    const downloadAdModalPath = this.downloadButtonTarget.dataset.dlAdModalPath;

    this.downloadButtonTarget.setAttribute('remote', 'true');

    window.addEventListener('TriggerDownload', this.triggerDownload, { once: true });

    getAjaxModal(downloadAdModalPath);
  }

  openDownloadLimitModal() {
    const downloadLimitModalPath = this.downloadButtonTarget.dataset.dlLimitModalPath;

    this.downloadButtonTarget.setAttribute('remote', 'true');

    getAjaxModal(downloadLimitModalPath);
  }

  spendCreditAndDownload = (event) => {
    // Need to reload the page so a new download button gets rendered
    this.triggerDownload(event, true);

    this.creditSpent = true;
  }

  confirmCreditSpend(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.creditSpent) {
      this.triggerDownload();
    } else {
      ezConfirm(
        event.currentTarget.getAttribute('data-confirm-text'),
        this.spendCreditAndDownload,
        ['Okay', 'Cancel'],
      );
    }
  }

  sendDownloadEvent() {
    let eventDetails = {
      resourceId: this.resourceId,
      resourceType: this.resourceType,
    };

    if (this.searchData) {
      eventDetails = Object.assign(eventDetails, this.searchData);
    }

    triggerCustomEvent(events.download_btn.initial_click, eventDetails);

    sendTrackingEvent({
      element: this.element,
      data: {
        action: DOWNLOAD_CLICK,
        label: getResourceType(),
        category: 'Show',
        content_type: `Content-${getContentType()}`,
      },
    });
  }

  sendDownloadCompleteEvent() {
    sendTrackingEvent({
      element: this.element,
      data: {
        action: DOWNLOAD_COMPLETE,
        label: getResourceType(),
        category: 'Show',
        content_type: `Content-${getContentType()}`,
      },
    });
  }

  joinProClick(event) {
    this.sendDownloadEvent();

    const category = findCategoryByElement(event.target);

    sendTrackingEvent({
      element: event.target,
      data: {
        action: JOIN_PRO_TRIGGERED,
        category,
        label: 'Pro',
      },
    });

    trackCompleteEvent({ action: JOIN_PRO_COMPLETE, category });
  }

  sendDownloadEventAndRedirect(event) {
    event.preventDefault();
    this.sendDownloadEvent();

    Turbolinks.visit(this.downloadButtonTarget.href);
  }

  get isTurbolinksPreview() {
    return document.documentElement.hasAttribute('data-turbolinks-preview');
  }

  get resourceId() {
    return document.querySelector('.ez-resource-show').dataset.resourceId;
  }

  get resourceType() {
    return document.querySelector('.ez-resource-show').dataset.resourceType;
  }

  get exceededDownloadLimit() {
    return this.dailyDownloadLimitValue && (this.dailyDownloadCountValue >= this.dailyDownloadLimitValue);
  }
}
